import { useRef, useState } from 'react'
import {
    Box,
    Button,
    CloseButton,
    Dialog,
    Flex,
    IconButton,
    Portal,
    Text,
} from '@chakra-ui/react'
import { LuCopy, LuDownload, LuQrCode } from 'react-icons/lu'
import { QRCodeCanvas } from 'qrcode.react'
import { toaster } from './ui/toaster'
import { useTranslation } from '../i18n'

/**
 * A row showing a URL with a copy button and a QR button that opens the QR in a
 * modal (with download). Optional `leading` content (e.g. a badge) is rendered
 * before the URL, and `children` after the QR button (e.g. a delete button).
 */
export function LinkWithQr({ url, qrTitle, downloadName = 'qr', copyLabel, leading, children }) {
    const { t } = useTranslation()
    const [qrOpen, setQrOpen] = useState(false)
    const qrRef = useRef(null)

    return (
        <>
            <Flex align="center" gap={2} bg="var(--mc-fill-soft)" p={2} borderRadius="md">
                {leading}
                <Text fontSize="xs" color="fg.muted" flex="1" userSelect="all" wordBreak="break-all">{url}</Text>
                <IconButton
                    aria-label={copyLabel || t('common.copyLink')}
                    size="xs"
                    variant="ghost"
                    onClick={() => {
                        navigator.clipboard.writeText(url)
                        toaster.create({ title: t('common.urlCopied'), status: 'success' })
                    }}
                >
                    <LuCopy />
                </IconButton>
                <IconButton
                    aria-label={t('events.showQrCode')}
                    size="xs"
                    variant="ghost"
                    onClick={() => setQrOpen(true)}
                >
                    <LuQrCode />
                </IconButton>
                {children}
            </Flex>

            <Dialog.Root open={qrOpen} onOpenChange={(e) => setQrOpen(e.open)}>
                <Portal>
                    <Dialog.Backdrop />
                    <Dialog.Positioner>
                        <Dialog.Content>
                            <Dialog.Header>
                                <Dialog.Title>{qrTitle}</Dialog.Title>
                            </Dialog.Header>
                            <Dialog.Body display="flex" justifyContent="center">
                                <Box bg="white" p={4} borderRadius="lg" ref={qrRef}>
                                    <QRCodeCanvas value={url} size={256} />
                                </Box>
                            </Dialog.Body>
                            <Dialog.Footer>
                                <Text fontSize="xs" color="fg.muted" flex="1" wordBreak="break-all">{url}</Text>
                                <Button size="sm" variant="outline" onClick={() => {
                                    const canvas = qrRef.current?.querySelector('canvas')
                                    if (!canvas) return
                                    const a = document.createElement('a')
                                    a.href = canvas.toDataURL('image/png')
                                    a.download = `${downloadName}.png`
                                    a.click()
                                }}>
                                    <LuDownload /> {t('common.download')}
                                </Button>
                            </Dialog.Footer>
                            <Dialog.CloseTrigger asChild>
                                <CloseButton size="sm" />
                            </Dialog.CloseTrigger>
                        </Dialog.Content>
                    </Dialog.Positioner>
                </Portal>
            </Dialog.Root>
        </>
    )
}

export default LinkWithQr
